import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  guardianKillSwitch,
  overviewSummary,
  policyEventsStream,
  policyGuardCatches,
  policyGuards,
  policyGuardStats,
  policyPosture,
  policyRequests,
  policyRuleReplay,
  policyRules,
  policyScope,
  policyScopeSummary,
  policyVersions,
  type ApiError,
  type PolicyGuard,
  type PolicyGuardFire,
  type PolicyGuardStat,
  type PolicyRequest,
  type PolicyRule,
  type RuleSpec,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { facilitiesParam, useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { useDemoBuilding } from '../../session/DemoScope';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { ErrorNote } from '../../ErrorNote';
import { Skeleton } from '../../ui/states';
import { ACTION_TONE, ACTIONS_SHORT, GROUPS, GUARD_COPY, TOPIC_CHOICES, groupOf, type GroupKey } from './catalog';
import { RuleBuilder, ruleSentence, ruleName } from './RuleBuilder';
import { FacilityMenu } from './FacilityMenu';
import { RequestDecision, RollbackDialog, RuleModeDialog } from './dialogs';
import { savePolicy, type Drafts } from './save';
import { actorWord, ago, barStyles, clock, dayClock, notServed, pre, ROLE_WORD, switchStyles, sx, useApi, useShellWidth, type PolicyRole } from './util';
import { BuildLabel, BuildTagged } from '../../ui/buildLabels';
import { tierLabel } from '../residents/common';

const SECTION_HEAD = 'font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:700;';
const EMPTY_DRAFTS: Drafts = { off: {}, edits: {}, fac: {}, rules: [], scope: {}, rollback: null };

interface Facility {
  id: string;
  name: string;
}

/** Guards (design pcV2 block, T12352; logic policyV2, L26047). */
export function GuardsTab({ pr, onHistory }: { pr: PolicyRole; onHistory: () => void }) {
  const { client, session } = useSession();
  const { role } = useConsoleRole();
  const demoBuilding = useDemoBuilding();
  const roReason = useReadOnlyReason();
  const pcW = useShellWidth();
  const isOp = pr === 'operator';
  const isFA = pr === 'facility';
  const canEdit = isOp || isFA;
  const blocked = roReason !== null;

  // The facility switcher (V6, design pcFac): one building picked there opens Policy on it; a
  // building chosen here wins until the switcher changes. A place that is not one building
  // keeps the network's settings, and the guard fires follow it.
  const [localFac, setFac] = useState<Facility | null>(null);
  const pickIds = usePickedIds();
  const { scope: picked, inView } = useFacilityScope();
  const pickKey = pickIds ? pickIds.join(',') : '';
  const swOne = !isFA && picked.fac && pickIds?.length === 1 ? { id: pickIds[0], name: inView.find((f) => f.id === pickIds[0])?.name ?? picked.fac } : null;
  const fac = localFac ?? swOne;
  const firesPick = !isFA && !fac && pickIds ? facilitiesParam(pickIds) : undefined;
  const facScoped = isFA || fac !== null;
  const scope = isFA ? undefined : fac ? fac.id : 'network';
  const canNet = isOp && !facScoped;
  // The facility picker reads GET /facilities/search, which answers the fleet roles only
  // (v3/finance/performance.ts: Super Admin, Operator, Partner); a Developer is refused
  // "Staff only", so the picker is not drawn for them.
  const canPickFacility = !isFA && (role === 'SUPER_ADMIN' || role === 'OPERATOR' || role === 'PARTNER');

  const guards = useApi((c, signal) => policyGuards(c, { query: { scope, facilities: firesPick }, signal }), [scope, firesPick]);
  const stats = useApi((c, signal) => policyGuardStats(c, { query: { scope, days: 7, facilities: firesPick }, signal }), [scope, firesPick]);
  const posture = useApi((c, signal) => policyPosture(c, { query: { scope }, signal }), [scope]);
  const requests = useApi((c, signal) => policyRequests(c, { signal }), []);
  const scopeQ = useApi((c, signal) => policyScope(c, { signal }), []);
  const versions = useApi((c, signal) => policyVersions(c, { query: { scope }, signal }), [scope]);
  const rules = useApi((c, signal) => policyRules(c, { query: { scope }, signal }), [scope]);
  const scopeSum = useApi((c, signal) => policyScopeSummary(c, { query: { scope }, signal }), [scope]);
  const summary = useApi((c, signal) => overviewSummary(c, { signal }), [], notServed(scopeSum.error));

  const [group, setGroup] = useState<GroupKey>('help');
  const [openId, setOpenId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [d, setD] = useState<Drafts>(EMPTY_DRAFTS);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [decide, setDecide] = useState<{ req: PolicyRequest; ok: boolean } | null>(null);
  const [liftOpen, setLiftOpen] = useState(false);
  const [rollback, setRollback] = useState<{ id: string; label: string } | null>(null);
  const [modeFor, setModeFor] = useState<PolicyRule | null>(null);
  const [feed, setFeed] = useState<PolicyGuardFire[]>([]);
  // The switcher changed: a building chosen here, an open guard and unsaved drafts belong to
  // the old view, so none of them carries over (a change can never land on the wrong building).
  useEffect(() => {
    setFac(null);
    setOpenId(null);
    setConfirmOpen(false);
    setD(EMPTY_DRAFTS);
  }, [pickKey]);

  // The live ticker: guard fires as they happen, opening with the last hour.
  useEffect(() => {
    const ctrl = new AbortController();
    setFeed([]);
    policyEventsStream(client, {
      scope,
      since: Date.now() - 3_600_000,
      signal: ctrl.signal,
      onFire: (f) => setFeed((p) => [f, ...p].slice(0, 20)),
    }).catch(() => {
      /* no live feed: the ticker says so */
    });
    return () => ctrl.abort();
  }, [client, session?.token, scope]);

  const reloadAll = () => {
    [guards, stats, posture, requests, scopeQ, versions, rules].forEach((q) => q.reload());
  };

  const statOf = useMemo(() => {
    const m = new Map<string, PolicyGuardStat>();
    (stats.data?.guards ?? []).forEach((s) => m.set(s.key, s));
    return m;
  }, [stats.data]);

  const live = guards.data?.guards ?? [];
  const floors = new Set(guards.data?.floors ?? []);
  const armedOf = (g: PolicyGuard) => (facScoped ? g.enabled !== !!d.fac[g.key] : g.enabled !== !!d.off[g.key]);
  const pctOf = (g: PolicyGuard) => statOf.get(g.key)?.pctOfTrip ?? null;
  const nearOf = (g: PolicyGuard) => {
    const p = pctOf(g);
    return p !== null && p >= 60;
  };
  const firesOf = (g: PolicyGuard) => statOf.get(g.key)?.firesPerDay ?? null;
  const tripsOf = (g: PolicyGuard) => (armedOf(g) ? (firesOf(g) ?? []).reduce((a, b) => a + b, 0) : 0);
  const disarmed = live.filter((g) => !armedOf(g));
  const nearList = live.filter((g) => armedOf(g) && nearOf(g));
  const firedWeek = live.reduce((a, g) => a + tripsOf(g), 0);
  const nearest = nearList.slice().sort((a, b) => (pctOf(b) ?? 0) - (pctOf(a) ?? 0))[0];
  const grpGuards = (k: GroupKey) => (k === 'mine' ? [] : live.filter((g) => groupOf(g.key) === k));

  const customAll = rules.data?.rules ?? [];
  const facId = isFA ? session?.user.organizationId ?? null : fac?.id ?? null;
  const custom = facScoped ? customAll.filter((r) => r.scope === 'network' || r.organizationId === facId) : customAll;
  const reqsAll = requests.data?.requests ?? [];

  // ── Posture (live state only; drafts never count) ──
  const p = posture.data;
  const pv = p?.posture ?? null;
  const PC = pv === 'HALTED' ? ['#B3261E', 'rgba(179,38,30,.12)'] : pv === 'DEGRADED' ? ['#3C454F', 'rgba(34,39,46,.14)'] : pv === 'SAFE' ? ['#1F6B52', 'rgba(31,107,82,.12)'] : ['#54606C', 'rgba(34,39,46,.08)'];
  const postureLine = !p
    ? ''
    : pv === null
      ? p.reason ?? 'Posture is not known right now.'
      : pv === 'HALTED'
        ? 'The kill switch froze all AI replies. Safety escalation and 911 stay live, so care is not interrupted. Only Aithora can lift it.'
        : pv === 'DEGRADED' && p.disarmed.length
          ? p.disarmed.length + (p.disarmed.length === 1 ? ' guard is' : ' guards are') + ' switched off: ' + p.disarmed.map((x) => x.name).join(', ') + '. Those protections are not running.'
          : pv === 'DEGRADED'
            ? p.detail.join(' ')
            : 'All ' + live.length + ' guards are running.' + (nearest ? ' Closest to tripping: ' + nearest.name + ', at ' + pctOf(nearest) + '% of its line.' : ' None close to tripping.');
  // Lifting a halt is the owner's release (the same call as RESUME). The server answers
  // canLift with an owner_release only for the owner; for anyone else it stays disabled.
  const canRelease = !!p && p.canLift.some((c) => c.how === 'owner_release');
  const liftTitle = roReason ?? (canRelease ? undefined : 'Only Aithora can lift a halt');
  // The switched-off guards this viewer may turn back on: a building's own, or the network's
  // for the Operator. A Facility Admin looking at network guards gets no button that does
  // nothing (QA-16).
  const reachable = p ? p.disarmed.filter((g) => (g.where === 'network' && canNet) || (g.where === 'facility' && facScoped)) : [];
  const postureAct: { l: string; f: () => void; off?: boolean; title?: string } | null =
    pv === 'HALTED'
      ? { l: 'Lift the halt', f: () => setLiftOpen(true), off: blocked || !canRelease, title: liftTitle }
      : pv === 'DEGRADED' && p && reachable.length && canEdit
      ? {
          l: 'Turn them back on',
          f: () =>
            setD((x) => {
              const n = { ...x, off: { ...x.off }, fac: { ...x.fac } };
              reachable.forEach((g) => {
                if (g.where === 'network') n.off[g.key] = true;
                else n.fac[g.key] = true;
              });
              return n;
            }),
        }
      : pv === 'SAFE' && nearest
        ? { l: 'Show ' + nearest.name, f: () => { setGroup(groupOf(nearest.key)); setOpenId(nearest.key); setShowNew(false); } }
        : null;

  // ── Drafts: what Review & save will send ──
  const scopeSaved = scopeQ.data?.settings ?? null;
  const sc = {
    blocked: d.scope.blocked ?? scopeSaved?.blockedTopics ?? [],
    hold: d.scope.hold !== undefined ? d.scope.hold : scopeSaved?.escalationHoldMinutes ?? null,
    draftH: d.scope.draftH !== undefined ? d.scope.draftH : scopeSaved?.draftTtlHours ?? null,
  };
  const setSc = (patch: Drafts['scope']) => {
    if (!canNet || blocked) return;
    setD((x) => ({ ...x, scope: { ...x.scope, ...patch } }));
  };
  const scDiff: { guard: string; what: string; change: string }[] = [];
  if (d.scope.blocked && d.scope.blocked.join('|') !== (scopeSaved?.blockedTopics ?? []).join('|'))
    scDiff.push({ guard: 'Blocked topics', what: 'never talks about', change: d.scope.blocked.join(', ') || 'none' });
  if (d.scope.hold !== undefined && d.scope.hold !== (scopeSaved?.escalationHoldMinutes ?? null))
    scDiff.push({ guard: 'Held escalations', what: 'escalate if unanswered after', change: (scopeSaved?.escalationHoldMinutes ?? '–') + ' → ' + d.scope.hold + ' min' });
  if (d.scope.draftH !== undefined && d.scope.draftH !== (scopeSaved?.draftTtlHours ?? null))
    scDiff.push({ guard: 'Drafts', what: 'discarded after', change: (scopeSaved?.draftTtlHours ?? '–') + ' → ' + d.scope.draftH + ' h' });
  const nameOfKey = (k: string) => live.find((g) => g.key === k)?.name ?? k;
  const facName = isFA ? scopeSum.data?.name ?? session?.user.orgName ?? 'your building' : fac?.name ?? '';
  const diffs = [
    ...Object.keys(d.edits).map((k) => {
      const [gk, field] = k.split(':');
      const g = live.find((x) => x.key === gk);
      const from = field === 'act' ? g?.network.action ?? '–' : String(g?.network.values[field] ?? '–');
      return { guard: nameOfKey(gk), what: field === 'act' ? 'response' : field, change: from + ' → ' + String(d.edits[k]) };
    }),
    ...Object.keys(d.off).map((k) => ({ guard: nameOfKey(k), what: 'running', change: live.find((g) => g.key === k)?.enabled ? 'on → off' : 'off → on' })),
    ...Object.keys(d.fac).map((k) => ({ guard: nameOfKey(k), what: 'in ' + facName, change: live.find((g) => g.key === k)?.enabled ? 'on → off' : 'off → on' })),
    ...d.rules.map((r) => ({ guard: 'New rule', what: (r.mode === 'watch' ? 'watch only' : 'enforced') + ' · ' + (facScoped ? facName : 'network'), change: ruleName(r) })),
    ...(d.rollback ? [{ guard: 'Roll back', what: 'to ' + d.rollback.label, change: 'loaded as a draft' }] : []),
    ...scDiff,
  ];
  const dirtyAll = diffs.length;
  const helpTouched = [...Object.keys(d.edits).map((k) => k.split(':')[0]), ...Object.keys(d.off), ...Object.keys(d.fac)].some((k) => groupOf(k) === 'help') || d.rules.some((r) => r.action === 'nurse' || r.action === 'human');

  const ss = scopeSum.data;
  const sumData = summary.data;
  const facilities = ss?.facilities ?? sumData?.facilities ?? null;
  const residents = ss?.residents ?? (facScoped && !isFA ? null : sumData?.residents ?? null);
  const resPart = residents === null ? '' : ' · ' + residents + ' residents';
  const facLine = isFA
    ? 'Your building: ' + facName + resPart
    : fac
      ? fac.name + resPart
      : facilities === null
        ? ''
        : 'All ' + facilities + ' facilities' + resPart;

  const doSave = async () => {
    if (blocked) return;
    setSaving(true);
    setSaveError(null);
    try {
      const note = await savePolicy(client, { isFA, canNet, scope: scope ?? null, drafts: d, guards: live, scopeSaved });
      setD(EMPTY_DRAFTS);
      setConfirmOpen(false);
      setSavedNote(note);
      reloadAll();
    } catch (e) {
      setSaveError(e as ApiError);
    } finally {
      setSaving(false);
    }
  };

  // ── Layout by the console-root width (design pcW) ──
  const cols = pcW >= 1500 ? '240px minmax(0,1fr) 290px' : pcW < 1240 ? '212px minmax(0,1fr)' : '256px minmax(0,1fr)';
  const areas = pcW >= 1500 ? '"g m r"' : '"g m" "r m"';
  const gRows = pcW >= 1500 ? 'minmax(0,1fr)' : 'minmax(0,2fr) minmax(0,3fr)';
  const showSpark = pcW >= 1240;
  const rowWrap = pcW >= 1240 ? 'nowrap' : 'wrap';

  const sel = GROUPS.find((g) => g.key === group) ?? GROUPS[0];
  const selGs = grpGuards(sel.key);
  const groupMeta =
    sel.key === 'mine'
      ? custom.length
        ? custom.filter((r) => r.mode === 'watch').length + ' watching · ' + custom.filter((r) => r.mode === 'enforce').length + ' enforced'
        : ''
      : selGs.filter(armedOf).length + ' of ' + selGs.length + ' running · tap a guard to change it';

  const segStyle = (on: boolean, tone: string) =>
    'display:inline-flex;align-items:center;min-height:36px;padding:0 13px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
    (on ? 'background:' + tone + ';color:#fff;' : 'color:#3C454F;border:1px solid rgba(34,39,46,.16);');

  const toggleOpen = (id: string) => (e: React.MouseEvent) => {
    pre(e);
    setOpenId(openId === id ? null : id);
  };

  const guardRows = selGs.map((g, i) => {
    const armed = armedOf(g);
    const near = armed && nearOf(g);
    const fires = firesOf(g);
    const t = armed ? (fires ?? []).reduce((a, b) => a + b, 0) : 0;
    const open = openId === g.key;
    const touched = Object.keys(d.edits).some((k) => k.indexOf(g.key + ':') === 0) || !!d.off[g.key];
    const floor = facScoped && floors.has(g.key);
    const hereOff = facScoped && !armed && g.network.enabled;
    const hereTouched = facScoped && !!d.fac[g.key];
    const pendingHere = facScoped && reqsAll.some((q) => q.status === 'pending' && (!facId || q.organizationId === facId) && q.payload?.overrides && g.key in q.payload.overrides);
    const tag = pendingHere ? 'PENDING AITHORA' : !armed ? (hereOff ? 'OFF HERE' : 'OFF') : touched || hereTouched ? 'DRAFT' : floor ? 'SET BY AITHORA' : near ? 'NEAR TRIP' : '';
    // design pv rows canArm: the network owner, or anyone who can edit a building's own guard (not a floor).
    const canArm = canNet || (facScoped && canEdit && !floor && g.network.enabled);
    const s1 = switchStyles(armed);
    const stat = statOf.get(g.key);
    const lastIso = stat?.lastFiredAt ?? null;
    const within7 = lastIso && Date.now() - new Date(lastIso).getTime() <= 7 * 86_400_000;
    const last = !armed ? 'Switched off' : stat && !stat.measured ? 'Not measured' : within7 ? ago(lastIso) : 'Not in 7 days';
    const pct = pctOf(g);
    const copy = GUARD_COPY[g.key];
    return (
      <GuardRow
        key={g.key}
        i={i}
        name={g.name}
        layer={g.layer}
        tag={tag}
        tagStyle={
          'font-size:14px;font-weight:800;letter-spacing:.06em;padding:1px 6px;border-radius:4px;' +
          (tag === 'OFF' || tag === 'OFF HERE' ? 'color:#54606C;background:rgba(34,39,46,.08);' : tag === 'SET BY AITHORA' ? 'color:#264C7E;background:rgba(47,92,151,.1);' : 'color:#3C454F;background:rgba(34,39,46,.14);')
        }
        sentence={copy?.sentence ?? ''}
        canArm={canArm}
        armTitle={armed ? 'Running' + (facScoped ? ' in ' + facName : '') + '. Tap to switch off (goes to draft).' : 'Off. Tap to switch on.'}
        onArm={() => {
          if (blocked) return;
          if (facScoped) setD((x) => { const n = { ...x.fac }; if (n[g.key]) delete n[g.key]; else n[g.key] = true; return { ...x, fac: n }; });
          else setD((x) => { const n = { ...x.off }; if (n[g.key]) delete n[g.key]; else n[g.key] = true; return { ...x, off: n }; });
        }}
        sw={s1}
        onOpen={toggleOpen(g.key)}
        open={open}
        spark={barStyles(fires ?? [0, 0, 0, 0, 0, 0, 0], near ? '#3C454F' : '#2F5C97', 20)}
        sparkTitle={fires ? t + ' fires in the last 7 days' : 'Not measured yet'}
        showSpark={showSpark}
        rowWrap={rowWrap}
        last={last}
        lastStyle={'font-size:16px;font-weight:700;white-space:nowrap;color:' + (!armed ? '#54606C' : t > 6 ? '#3C454F' : '#22272E') + ';'}
        cap={armed && pct !== null ? pct + '% of trip line' : ''}
        wrap={'border-bottom:1px solid rgba(34,39,46,.10);padding:8px 0;' + (touched ? 'box-shadow:inset 3px 0 0 #3C454F;padding-left:10px;' : '') + (armed ? '' : 'opacity:.72;') + 'animation:riseIn .3s cubic-bezier(.22,.61,.36,1) ' + Math.min(280, i * 40) + 'ms backwards;'}
      >
        {open && (
          <GuardDetail
            g={g}
            scope={scope}
            facilities={firesPick}
            armed={armed}
            canNet={canNet && !blocked}
            edits={d.edits}
            onEdit={(k, v) =>
              setD((x) => {
                const n = { ...x.edits };
                const [gk, field] = k.split(':');
                const base = field === 'act' ? g.network.action : g.network.values[field];
                if (base === v) delete n[k];
                else n[k] = v;
                void gk;
                return { ...x, edits: n };
              })
            }
            who={
              floor
                ? 'A safety floor, set by Aithora for every building. It cannot be switched off or loosened here.'
                : facScoped
                  ? canEdit && isFA
                    ? 'Optional for ' + facName + '. You can switch it off here; the settings are set network-wide by Aithora.'
                    : 'Set network-wide by Aithora.'
                  : 'Network-wide. Only an Aithora operator can change this, and saves need approval.'
            }
            segStyle={segStyle}
          />
        )}
      </GuardRow>
    );
  });

  const ruleRows = custom.map((r, i) => {
    const open = openId === 'c' + r.id;
    const s1 = switchStyles(r.mode === 'enforce');
    const canArm = r.status === 'live' && (isOp || (isFA && r.organizationId === facId));
    return (
      <GuardRow
        key={r.id}
        i={i}
        name={ruleName(r)}
        layer={r.scope === 'network' ? 'NETWORK' : facName.toUpperCase()}
        tag={r.status === 'pending' ? 'PENDING AITHORA' : r.mode === 'watch' ? 'WATCH ONLY' : 'ENFORCED'}
        tagStyle={'font-size:14px;font-weight:800;letter-spacing:.06em;padding:1px 6px;border-radius:4px;' + (r.mode === 'watch' ? 'color:#264C7E;background:rgba(47,92,151,.1);' : 'color:#1F6B52;background:rgba(31,107,82,.12);')}
        sentence={ruleSentence(r)}
        canArm={canArm}
        armTitle={r.mode === 'watch' ? 'Watching only. Tap to enforce.' : 'Enforcing. Tap to go back to watch-only.'}
        onArm={() => !blocked && setModeFor(r)}
        sw={s1}
        onOpen={toggleOpen('c' + r.id)}
        open={open}
        spark={barStyles([0, 0, 0, 0, 0, 0, 0], '#2F5C97', 20)}
        sparkTitle="Replay the rule to see its matches"
        showSpark={showSpark}
        rowWrap={rowWrap}
        last={r.status === 'pending' ? 'Waiting' : r.mode === 'watch' ? 'Watching' : 'Enforcing'}
        lastStyle="font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;"
        cap={r.createdAt ? 'added ' + dayClock(r.createdAt) : ''}
        wrap={'border-bottom:1px solid rgba(34,39,46,.10);padding:8px 0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1) ' + Math.min(240, i * 40) + 'ms backwards;'}
      >
        {open && <RuleDetail r={r} segStyle={segStyle} />}
      </GuardRow>
    );
  });

  const pendingRuleRows = d.rules.map((r, i) => (
    <GuardRow
      key={'draft' + i}
      i={i}
      name={ruleName(r)}
      layer={facScoped ? facName.toUpperCase() : 'NETWORK'}
      tag={'DRAFT · ' + (r.mode === 'watch' ? 'WATCH ONLY' : 'ENFORCED')}
      tagStyle={'font-size:14px;font-weight:800;letter-spacing:.06em;padding:1px 6px;border-radius:4px;' + (r.mode === 'watch' ? 'color:#264C7E;background:rgba(47,92,151,.1);' : 'color:#1F6B52;background:rgba(31,107,82,.12);')}
      sentence={ruleSentence(r)}
      canArm={false}
      armTitle=""
      onArm={() => undefined}
      sw={switchStyles(r.mode === 'enforce')}
      onOpen={toggleOpen('d' + i)}
      open={openId === 'd' + i}
      spark={barStyles([0, 0, 0, 0, 0, 0, 0], '#2F5C97', 20)}
      sparkTitle="Not saved yet"
      showSpark={showSpark}
      rowWrap={rowWrap}
      last="Not saved yet"
      lastStyle="font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;"
      cap=""
      wrap="border-bottom:1px solid rgba(34,39,46,.10);padding:8px 0;box-shadow:inset 3px 0 0 #3C454F;padding-left:10px;"
    >
      {openId === 'd' + i && (
        <div style={sx('display:flex;gap:10px;padding:10px 0 6px;')}>
          <a href="#" onClick={(e) => { pre(e); setD((x) => ({ ...x, rules: x.rules.filter((_, j) => j !== i) })); setOpenId(null); }} style={sx('align-self:flex-start;display:inline-flex;align-items:center;min-height:36px;font-size:16px;font-weight:700;color:#B3261E;text-decoration:none;')}>
            Delete this rule
          </a>
        </div>
      )}
    </GuardRow>
  ));

  // ── Right column: requests, scope, recent changes ──
  const myReqs = reqsAll;
  const pendingN = myReqs.filter((q) => q.status === 'pending').length;
  const QS = { pending: ['PENDING', '#3C454F', 'rgba(34,39,46,.14)'], approved: ['APPROVED', '#1F6B52', 'rgba(31,107,82,.12)'], declined: ['DECLINED', '#B3261E', 'rgba(179,38,30,.12)'] } as const;
  const queue = myReqs.slice().sort((x, y) => (x.status === 'pending' ? 0 : 1) - (y.status === 'pending' ? 0 : 1)).slice(0, 6);
  const chipStyle = (on: boolean, tone: string) =>
    'display:inline-flex;align-items:center;gap:6px;min-height:34px;padding:0 11px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' +
    (on ? 'background:' + tone + ';color:#fff;' : 'color:#3C454F;border:1px solid rgba(34,39,46,.18);');
  const topics = [...TOPIC_CHOICES, ...sc.blocked.filter((t) => !TOPIC_CHOICES.includes(t))];
  const canScope = canNet;
  const expiries: [string, 'hold' | 'draftH', number | null, string, number, number, number][] = [
    ['An unanswered escalation moves up the chain after', 'hold', sc.hold, ' min', 5, 5, 120],
    ['Unsaved drafts are discarded after', 'draftH', sc.draftH, ' h', 1, 1, 72],
  ];
  const savedOf = (k: 'hold' | 'draftH') => (k === 'hold' ? scopeSaved?.escalationHoldMinutes ?? null : scopeSaved?.draftTtlHours ?? null);
  const vers = versions.data?.versions ?? [];
  const recent = vers.slice(0, 4);

  const ready = guards.data && stats.data;
  const mainError = guards.error ?? stats.error;

  return (
    <div data-screen-label="Policy · Guards" style={sx('height:100%;min-height:0;display:flex;flex-direction:column;gap:10px;min-width:0;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px;min-width:0;position:relative;z-index:40;')}>
        <span data-testid="policy-facline" style={sx('flex:0 0 auto;font-size:16px;font-weight:700;color:#22272E;white-space:nowrap;')}>{facLine}</span>
        {canPickFacility && (
          <FacilityMenu
            current={fac}
            buildings={facilities}
            onPick={(f) => {
              setFac(f);
              setOpenId(null);
              setConfirmOpen(false);
              setD(EMPTY_DRAFTS);
            }}
          />
        )}
      </div>

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('display:inline-flex;flex-direction:column;gap:1px;flex:0 0 auto;')}>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;white-space:nowrap;')}>SYSTEM POSTURE</span>
          <span data-testid="policy-posture" style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:800;letter-spacing:.1em;white-space:nowrap;color:' + PC[0] + ';background:' + PC[1] + ';')}>
            <span style={sx('width:8px;height:8px;border-radius:50%;background:' + PC[0] + ';animation:breathe 1.8s ease-in-out infinite;')} />
            {p ? pv ?? 'UNKNOWN' : '–'}
          </span>
        </span>
        <span style={sx('font-size:16px;font-weight:600;color:#22272E;min-width:0;flex:1 1 280px;line-height:1.4;text-wrap:pretty;')}>{postureLine}</span>
        {postureAct && canEdit && (
          <a href="#" aria-disabled={postureAct.off || undefined} title={postureAct.title} onClick={(e) => { pre(e); if (!postureAct.off) postureAct.f(); }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:10px;background:#22272E;color:#FCFAF4;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}>
            {postureAct.l}
          </a>
        )}
        <span style={sx('display:flex;align-items:baseline;gap:6px 18px;flex-wrap:wrap;')}>
          {ready &&
            [
              { k: 'running', v: String(live.length - disarmed.length), c: disarmed.length ? '#3C454F' : '#22272E' },
              { k: 'near trip', v: String(nearList.length), c: nearList.length ? '#3C454F' : '#22272E' },
              { k: 'fires this week', v: String(firedWeek), c: '#22272E' },
            ].map((t) => (
              <span key={t.k} style={sx('display:inline-flex;align-items:baseline;gap:6px;white-space:nowrap;')}>
                <span style={sx('font-size:21px;font-weight:800;letter-spacing:-.03em;font-variant-numeric:tabular-nums;color:' + t.c + ';')}>{t.v}</span>
                <span style={sx('font-size:16px;color:#3C454F;')}>{t.k}</span>
              </span>
            ))}
        </span>
      </div>

      <Ticker feed={feed} />

      <div style={sx('flex:1 1 auto;min-height:0;display:grid;grid-template-columns:' + cols + ';grid-template-areas:' + areas + ';grid-template-rows:' + gRows + ';gap:14px 22px;min-width:0;')}>
        <div style={sx('grid-area:g;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:2px;min-width:0;')}>
          <div style={sx('font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:700;padding:2px 12px 6px;')}>What it protects</div>
          {GROUPS.map((g) => {
            const gs = grpGuards(g.key);
            const near = gs.filter((x) => armedOf(x) && nearOf(x)).length;
            const trips = g.key === 'mine' ? 0 : gs.reduce((a, x) => a + tripsOf(x), 0);
            const served = stats.data?.groups?.find((x) => x.key === g.key)?.firesPerDay;
            const series = g.key === 'mine' ? [0, 0, 0, 0, 0, 0, 0] : served ?? [0, 1, 2, 3, 4, 5, 6].map((day) => gs.reduce((a, x) => a + (armedOf(x) ? firesOf(x)?.[day] ?? 0 : 0), 0));
            const on = !showNew && group === g.key;
            return (
              <a
                key={g.key}
                href="#"
                aria-current={on ? 'true' : undefined}
                onClick={(e) => { pre(e); setGroup(g.key); setShowNew(false); setOpenId(null); }}
                style={sx('display:flex;flex-direction:column;gap:3px;padding:10px 12px;border-radius:10px;text-decoration:none;color:inherit;min-width:0;' + (on ? 'background:rgba(47,92,151,.09);box-shadow:inset 3px 0 0 #2F5C97;' : ''))}
              >
                <span style={sx('display:flex;align-items:baseline;gap:8px;min-width:0;')}>
                  <span style={sx('font-size:16px;font-weight:800;color:#22272E;flex:1 1 auto;min-width:0;line-height:1.3;text-wrap:balance;')}>{g.name}</span>
                  <span style={sx('font-size:14px;font-weight:800;letter-spacing:.04em;white-space:nowrap;color:#3C454F;')}>{near ? near + ' near' : ''}</span>
                </span>
                <span style={sx('display:flex;align-items:flex-end;gap:8px;min-width:0;')}>
                  <span style={sx('font-size:14px;color:#54606C;line-height:1.4;flex:1 1 auto;min-width:0;')}>
                    {g.key === 'mine' ? (custom.length ? custom.length + (custom.length === 1 ? ' rule' : ' rules') : 'None yet') : gs.length + ' guards · ' + trips + ' fires this week'}
                  </span>
                  <span style={sx('display:flex;align-items:flex-end;gap:2px;height:16px;flex:0 0 auto;')}>
                    {barStyles(series, g.key === 'money' ? '#B3261E' : '#2F5C97', 16).map((s, i) => (
                      <span key={i} style={sx(s)} />
                    ))}
                  </span>
                </span>
              </a>
            );
          })}
          {canEdit && (
            <>
              <a
                href="#"
                aria-disabled={blocked || undefined}
                title={roReason ?? undefined}
                onClick={(e) => { pre(e); if (!blocked) { setShowNew(true); } }}
                style={sx('display:flex;align-items:center;justify-content:center;gap:8px;min-height:44px;margin:8px 0 0;border-radius:10px;border:1.5px dashed rgba(47,92,151,.5);color:#264C7E;font-size:16px;font-weight:700;text-decoration:none;')}
              >
                + New rule
              </a>
              <span style={sx('font-size:14px;color:#54606C;line-height:1.45;padding:6px 4px 0;text-wrap:pretty;')}>Describe it in plain words and Atlas drafts it. New rules start in watch-only mode.</span>
            </>
          )}
          {!canEdit && (
            <div style={sx('display:flex;flex-direction:column;gap:8px;padding:10px 12px 0;')}>
              <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>{'Read-only as ' + tierLabel(role) + '.'}</span>
              <a
                href="#"
                aria-disabled="true"
                title="Your role cannot send policy change requests. A Facility Admin asks for their own building; Aithora staff change the network."
                onClick={pre}
                style={sx('display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 14px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;color:#264C7E;border:1px solid rgba(47,92,151,.4);')}
              >
                Request a change
              </a>
            </div>
          )}
        </div>

        <div style={sx('grid-area:r;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:14px;min-width:0;padding-right:3px;')}>
          <div data-testid="policy-queue" style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;')}>
            <span style={sx('display:flex;align-items:baseline;gap:8px;')}>
              <span style={sx(SECTION_HEAD)}>{isFA ? 'Your requests to Aithora' : 'Pending approvals'}</span>
              <span style={sx('font-size:16px;font-weight:800;color:' + (pendingN ? '#3C454F' : '#54606C') + ';')}>{requests.data ? String(pendingN) : ''}</span>
            </span>
            {requests.error && !requests.data && <ErrorNote error={requests.error} onRetry={requests.reload} compact />}
            {requests.data && myReqs.length === 0 && (
              <span style={sx('font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
                {isFA ? 'Every change you save goes to Aithora compliance for approval. Nothing waiting right now.' : 'No facility is waiting on you.'}
              </span>
            )}
            {queue.map((q) => {
              const shown = actorWord(pr, q.requestedByName, q.requestedByRole, null);
              const who = shown === 'Aithora' ? shown : q.requestedByName ?? null;
              const roleWord = shown === 'Aithora' ? null : q.requestedByRole ? ROLE_WORD[q.requestedByRole] ?? q.requestedByRole : null;
              const by = [who, roleWord].filter(Boolean).join(', ') || 'Unknown requester';
              const st = QS[q.status];
              return (
                <div key={q.id} style={sx('display:flex;flex-direction:column;gap:4px;padding:8px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;animation:riseIn .26s cubic-bezier(.22,.61,.36,1);')}>
                  <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                    <b style={sx('font-size:16px;color:#22272E;')}>{q.facilityName ?? 'Network'}</b>
                    {q.sample === true && !demoBuilding && <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;')}>SAMPLE</span></BuildLabel>}
                    <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;padding:1px 6px;border-radius:4px;margin-left:auto;color:' + st[1] + ';background:' + st[2] + ';')}>{st[0]}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{by}</span> {'·'} <span>{dayClock(q.requestedAt)}</span>
                  </span>
                  {q.items.map((it, k) => (
                    <span key={k} style={sx('font-size:16px;color:#22272E;line-height:1.4;')}>
                      <b style={sx('font-weight:700;')}>{it.guard}</b> <span style={sx('color:#54606C;')}>{it.change}</span>
                    </span>
                  ))}
                  {q.status === 'declined' && q.reason && <span style={sx('font-size:14px;color:#B3261E;line-height:1.4;')}>{q.reason}</span>}
                  {/* Only Andrew (Super Admin) approves a policy change; Support reads the queue. */}
                  {isOp && q.status === 'pending' && role !== 'SUPER_ADMIN' && (
                    <span data-testid="policy-andrew-approves" style={sx('font-size:14px;color:#54606C;margin-top:2px;')}>View only · Andrew approves</span>
                  )}
                  {role === 'SUPER_ADMIN' && q.status === 'pending' && (
                    <span style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:2px;')}>
                      <a href="#" aria-disabled={blocked || undefined} title={roReason ?? 'Only the owner approves. The server checks.'} onClick={(e) => { pre(e); if (!blocked) setDecide({ req: q, ok: true }); }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:9px;background:#1F6B52;color:#fff;font-size:16px;font-weight:700;text-decoration:none;')}>
                        Approve
                      </a>
                      <a href="#" aria-disabled={blocked || undefined} title={roReason ?? 'Only the owner declines. The server checks.'} onClick={(e) => { pre(e); if (!blocked) setDecide({ req: q, ok: false }); }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:9px;border:1px solid rgba(179,38,30,.4);color:#B3261E;font-size:16px;font-weight:700;text-decoration:none;')}>
                        Decline
                      </a>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;border-top:1px solid rgba(34,39,46,.12);padding-top:10px;')}>
            <span style={sx(SECTION_HEAD + 'margin-top:4px;')}>Never talks about</span>
            <span data-testid="policy-topics" style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
              {scopeQ.data &&
                topics.map((k) => {
                  const on = sc.blocked.includes(k);
                  return (
                    <a
                      key={k}
                      href="#"
                      aria-pressed={on}
                      aria-disabled={!canScope || blocked || undefined}
                      title={on ? 'Blocked. Tap to allow.' : 'Allowed. Tap to block.'}
                      onClick={(e) => { pre(e); setSc({ blocked: on ? sc.blocked.filter((x) => x !== k) : sc.blocked.concat([k]) }); }}
                      style={sx(chipStyle(on, '#B3261E'))}
                    >
                      {on ? k + '  ×' : '+ ' + k}
                    </a>
                  );
                })}
            </span>
            <span style={sx(SECTION_HEAD + 'margin-top:4px;')}>Time limits</span>
            {expiries.map((x) => {
              const v = x[2];
              const changed = v !== savedOf(x[1]);
              return (
                <span key={x[1]} style={sx('display:flex;align-items:center;gap:10px;min-width:0;')}>
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#3C454F;line-height:1.35;text-wrap:pretty;')}>{x[0]}</span>
                  <span style={sx('display:inline-flex;align-items:center;border:1px solid rgba(34,39,46,.18);border-radius:10px;overflow:hidden;flex:0 0 auto;')}>
                    {canScope && (
                      <a href="#" aria-label="Less" aria-disabled={blocked || undefined} onClick={(e) => { pre(e); setSc({ [x[1]]: Math.max(x[5], (v ?? x[5]) - x[4]) }); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:34px;height:38px;font-size:16px;font-weight:700;color:#264C7E;text-decoration:none;')}>
                        {'−'}
                      </a>
                    )}
                    <span style={sx('min-width:54px;text-align:center;font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (changed ? '#3C454F' : '#22272E') + ';')}>{scopeQ.data ? (v === null ? 'Not set' : v + x[3]) : ''}</span>
                    {canScope && (
                      <a href="#" aria-label="More" aria-disabled={blocked || undefined} onClick={(e) => { pre(e); setSc({ [x[1]]: Math.min(x[6], (v ?? x[5] - x[4]) + x[4]) }); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:34px;height:38px;font-size:16px;font-weight:700;color:#264C7E;text-decoration:none;')}>
                        +
                      </a>
                    )}
                  </span>
                </span>
              );
            })}
          </div>
          <div style={sx('display:flex;flex-direction:column;gap:4px;min-width:0;border-top:1px solid rgba(34,39,46,.12);padding-top:10px;')}>
            <span style={sx(SECTION_HEAD)}>Recent changes</span>
            {versions.data && recent.length === 0 && <span style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>Nothing saved this session. Every save lands here with who made it.</span>}
            {recent.map((v) => {
              const isCurrent = v.status === 'live';
              const canRoll = isOp && !blocked && !isCurrent && v.status !== 'draft' && v.status !== 'expired';
              const by = actorWord(pr, v.createdByName, v.createdByRole);
              return (
                <span key={v.id} style={sx('display:flex;align-items:center;gap:8px;min-height:36px;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                  <span style={sx('display:flex;flex-direction:column;min-width:0;flex:1 1 auto;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
                      {'v' + v.versionNo} {'·'} {clock(v.approvedAt ?? v.createdAt ?? new Date().toISOString())}
                    </span>
                    <span style={sx('font-size:14px;color:#54606C;')}>{(v.summary ?? v.status) + ' · by ' + by}</span>
                  </span>
                  {canRoll && (
                    <a href="#" onClick={(e) => { pre(e); setRollback({ id: v.id, label: 'v' + v.versionNo }); }} style={sx('font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}>
                      Roll back
                    </a>
                  )}
                  {isCurrent && <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;color:#1F6B52;')}>LIVE</span>}
                </span>
              );
            })}
            {vers.length > 4 && (
              <a href="#" onClick={(e) => { pre(e); onHistory(); }} style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;')}>
                All versions
              </a>
            )}
          </div>
        </div>

        <div style={sx('grid-area:m;min-height:0;overflow-y:auto;min-width:0;display:flex;flex-direction:column;padding-right:3px;')}>
          {!showNew && (
            <>
              <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);')}>
                <span style={sx('font-size:17px;font-weight:800;letter-spacing:-.01em;color:#22272E;min-width:0;text-wrap:pretty;')}>{sel.question}</span>
                <span style={sx('font-size:16px;color:#54606C;')}>{ready ? groupMeta : ''}</span>
              </div>
              {mainError && !ready && <div style={sx('padding:14px 0;')}><ErrorNote error={mainError} onRetry={() => { guards.reload(); stats.reload(); }} /></div>}
              {!ready && !mainError && <div style={sx('padding:14px 0;')}><Skeleton rows={6} label="Loading guards" /></div>}
              {ready && sel.key === 'mine' && custom.length === 0 && d.rules.length === 0 && (
                <div style={sx('display:flex;flex-direction:column;gap:10px;align-items:flex-start;padding:22px 2px;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>No rules of your own yet.</span>
                  <span style={sx('font-size:16px;color:#54606C;line-height:1.5;max-width:60ch;text-wrap:pretty;')}>
                    The built-in guards cover the common risks. Add a rule when your building has one of its own, like no conversations about money with residents in their first month.
                  </span>
                </div>
              )}
              {ready && (sel.key === 'mine' ? [...pendingRuleRows, ...ruleRows] : guardRows)}
            </>
          )}
          {showNew && (
            <RuleBuilder
              onCancel={() => setShowNew(false)}
              guards={live}
              armedOf={armedOf}
              onAdd={(r: RuleSpec) => {
                setD((x) => ({ ...x, rules: [...x.rules, r] }));
                setShowNew(false);
                setGroup('mine');
                setOpenId('d' + d.rules.length);
              }}
            />
          )}
        </div>
      </div>

      {canEdit && (
        <div style={sx('flex:0 0 auto;border-top:1px solid rgba(34,39,46,.14);padding-top:8px;display:flex;flex-direction:column;gap:8px;min-width:0;')}>
          {confirmOpen && dirtyAll > 0 && (
            <div role="dialog" aria-label="Before you save" style={sx('display:flex;flex-direction:column;gap:8px;padding:10px 12px;border-radius:10px;background:rgba(34,39,46,.08);animation:riseIn .22s cubic-bezier(.22,.61,.36,1);min-width:0;')}>
              <span style={sx('display:flex;align-items:baseline;gap:6px 14px;flex-wrap:wrap;')}>
                <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#3C454F;')}>BEFORE YOU SAVE</span>
                <span style={sx('font-size:16px;font-weight:700;color:#22272E;flex:1 1 260px;min-width:0;text-wrap:pretty;')}>
                  {(facScoped
                    ? 'Applies to 1 facility, ' + facName + (residents !== null ? ', and its ' + residents + ' residents. ' : '. ')
                    : 'Applies to all ' + (facilities ?? '') + ' facilities' + (residents !== null ? ' and ' + residents + ' residents. ' : '. ')) +
                    (helpTouched ? 'This changes how people get help, so the escalation path is affected.' : 'The escalation path is not affected.') +
                    (isFA ? ' Goes to Aithora compliance for approval; nothing changes until they approve.' : ' Aithora changes go live when you confirm.')}
                </span>
              </span>
              {diffs.map((x, k) => (
                <span key={k} style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;font-size:16px;min-width:0;')}>
                  <b style={sx('color:#22272E;')}>{x.guard}</b>
                  <span style={sx('color:#54606C;')}>{x.what}</span>
                  <span style={sx('font-weight:700;font-variant-numeric:tabular-nums;color:#22272E;')}>{x.change}</span>
                </span>
              ))}
              {saveError && <ErrorNote error={saveError} compact />}
              <span style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
                <a href="#" aria-disabled={saving || blocked || undefined} title={roReason ?? undefined} onClick={(e) => { pre(e); if (!saving) void doSave(); }} style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:10px;background:#22272E;color:#FCFAF4;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}>
                  {saving ? 'Saving…' : isFA ? 'Send for approval' : 'Confirm and save'}
                </a>
                <a href="#" onClick={(e) => { pre(e); setConfirmOpen(false); }} style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 6px;font-size:16px;font-weight:700;color:#54606C;text-decoration:none;')}>
                  Keep editing
                </a>
                <span style={sx('font-size:14px;color:#54606C;flex:1 1 200px;min-width:0;')}>
                  {isFA ? 'Aithora compliance reviews every facility change. You will see the answer under Your requests.' : 'Recorded in History with your name and the time.'}
                </span>
              </span>
            </div>
          )}
          <div style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;min-width:0;')}>
            <span data-testid="policy-dirty" style={sx('white-space:nowrap;font-size:16px;' + (dirtyAll ? 'color:#3C454F;font-weight:700;' : 'color:#54606C;'))}>
              {dirtyAll === 0 ? savedNote ?? 'No unsaved changes' : dirtyAll + (dirtyAll === 1 ? ' unsaved change' : ' unsaved changes')}
            </span>
            <span style={sx('font-size:14px;color:#54606C;')}>
              {vers[0] ? 'Last saved ' + clock(vers[0].approvedAt ?? vers[0].createdAt ?? new Date().toISOString()) + ' by ' + actorWord(pr, vers[0].createdByName, vers[0].createdByRole) : ''}
            </span>
            <span style={sx('flex:1 1 10px;')} />
            <a href="#" data-testid="guards-discard" aria-disabled={!dirtyAll || undefined} title={dirtyAll ? undefined : 'No unsaved changes to discard'} onClick={(e) => { pre(e); if (dirtyAll) { setD(EMPTY_DRAFTS); setConfirmOpen(false); } }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 12px;font-size:16px;font-weight:700;text-decoration:none;color:' + (dirtyAll ? '#3C454F' : 'rgba(84,96,108,.6);cursor:default') + ';')}>
              Discard
            </a>
            <a
              href="#"
              aria-disabled={blocked || !dirtyAll || undefined}
              title={roReason ?? undefined}
              onClick={(e) => { pre(e); if (dirtyAll && !blocked) { setSaveError(null); setConfirmOpen(true); } }}
              style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (dirtyAll ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.08);color:#54606C;'))}
            >
              Review &amp; save
            </a>
          </div>
        </div>
      )}

      {liftOpen && (
        <ConfirmDialog
          title="Turn the AI back on everywhere?"
          confirmLabel="Lift the halt"
          body={<p>Only the owner can turn the AI back on. The server checks.</p>}
          onConfirm={async () => {
            await guardianKillSwitch(client, { body: withConfirm({ action: 'release', scope: 'global' }) });
            posture.reload();
          }}
          onClose={() => setLiftOpen(false)}
        />
      )}
      {decide && <RequestDecision req={decide.req} ok={decide.ok} onClose={() => setDecide(null)} onDone={() => { requests.reload(); versions.reload(); guards.reload(); posture.reload(); }} />}
      {rollback && (
        <RollbackDialog
          id={rollback.id}
          label={rollback.label}
          onClose={() => setRollback(null)}
          onDone={(draftId) => {
            setD((x) => ({ ...x, rollback: { id: draftId, label: rollback.label } }));
            versions.reload();
          }}
        />
      )}
      {modeFor && <RuleModeDialog rule={modeFor} isOp={isOp} onClose={() => setModeFor(null)} onDone={() => { rules.reload(); requests.reload(); }} />}
    </div>
  );
}

/** The ticker under the posture (design pv.feed). Real fires only; says so when there are none. */
function Ticker({ feed }: { feed: PolicyGuardFire[] }) {
  const demoBuilding = useDemoBuilding();
  const toneOf = (f: PolicyGuardFire): [string, string] => {
    const a = String(f.action ?? '').toLowerCase();
    if (groupOf(f.guard) === 'money' && /held|block|stop|frozen/.test(a)) return ['▲', '#B3261E'];
    if (/escalat|paged|page|nurse|911/.test(a)) return ['●', '#2F5C97'];
    if (/clear|recover|normal|fresh|resolved/.test(a)) return ['●', '#1F6B52'];
    if (/held|hold|stop|block|deny|denied/.test(a)) return ['●', '#3C454F'];
    return ['●', '#54606C'];
  };
  const sample = !demoBuilding && feed.some((f) => f.real === false);
  const items = feed.length ? feed.concat(feed) : [];
  return (
    <div data-testid="policy-ticker" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:10px;min-width:0;height:32px;border-top:1px solid rgba(34,39,46,.12);border-bottom:1px solid rgba(34,39,46,.12);overflow:hidden;')}>
      <span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:800;letter-spacing:.1em;color:#1F6B52;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#1F6B52;animation:breathe 1.6s ease-in-out infinite;')} />
        LIVE
      </span>
      {sample && <BuildLabel><span style={sx('flex:0 0 auto;font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;')}>SAMPLE FEED</span></BuildLabel>}
      <div style={sx('flex:1 1 auto;min-width:0;overflow:hidden;-webkit-mask-image:linear-gradient(90deg,transparent,#000 3%,#000 97%,transparent);mask-image:linear-gradient(90deg,transparent,#000 3%,#000 97%,transparent);')}>
        {items.length === 0 ? (
          <span style={sx('font-size:16px;color:#54606C;white-space:nowrap;padding-left:12px;')}>No guard has fired in the last hour.</span>
        ) : (
          <div style={sx('display:inline-flex;gap:36px;white-space:nowrap;animation:pcMarq 80s linear infinite;')}>
            {items.map((f, i) => {
              const [mark, c] = toneOf(f);
              const who = [f.resident?.initials, f.resident?.room ? 'Rm ' + f.resident.room : null, f.detail].filter(Boolean).join(' · ');
              return (
                <span key={i} style={sx('display:inline-flex;gap:7px;align-items:baseline;font-size:16px;')}>
                  <span style={sx('color:' + c + ';font-weight:800;')}>{mark}</span>
                  <b style={sx('color:#22272E;font-weight:700;')}>{f.what ?? f.guardName}</b>
                  <span style={sx('color:#54606C;')}>
                    {who ? <span>{who}</span> : null}
                    {who ? ' · ' : ''}
                    <span>{ago(f.at)}</span>
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function GuardRow(props: {
  i: number;
  name: string;
  layer: string;
  tag: string;
  tagStyle: string;
  sentence: string;
  canArm: boolean;
  armTitle: string;
  onArm: () => void;
  sw: { wrap: string; knob: string };
  onOpen: (e: React.MouseEvent) => void;
  open: boolean;
  spark: string[];
  sparkTitle: string;
  showSpark: boolean;
  rowWrap: string;
  last: string;
  lastStyle: string;
  cap: string;
  wrap: string;
  children?: ReactNode;
}) {
  const p = props;
  return (
    <div style={sx(p.wrap)} data-testid="policy-row">
      <div style={sx('display:flex;align-items:center;gap:4px 12px;flex-wrap:' + p.rowWrap + ';min-height:44px;')}>
        {p.canArm && (
          <a
            href="#"
            role="switch"
            aria-checked={p.sw.wrap.includes('#1F6B52')}
            aria-label={p.name}
            title={p.armTitle}
            onClick={(e) => { pre(e); p.onArm(); }}
            style={sx(p.sw.wrap)}
          >
            <span style={sx(p.sw.knob)} />
          </a>
        )}
        <a href="#" onClick={p.onOpen} aria-expanded={p.open} style={sx('flex:1 1 220px;min-width:0;display:flex;flex-direction:column;gap:2px;text-decoration:none;color:inherit;')}>
          <span style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:nowrap;min-width:0;')}>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;flex:0 1 auto;min-width:0;overflow-wrap:break-word;')}>{p.name}</span>
            <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;white-space:nowrap;flex:0 0 auto;')}>{p.layer}</span>
            {p.tag && <BuildTagged tag={p.tag}><span style={sx('white-space:nowrap;flex:0 0 auto;' + p.tagStyle)}>{p.tag}</span></BuildTagged>}
          </span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;min-width:0;overflow-wrap:break-word;')}>{p.sentence}</span>
        </a>
        <a href="#" onClick={p.onOpen} tabIndex={-1} style={sx('flex:0 0 auto;display:flex;align-items:center;gap:12px;text-decoration:none;color:inherit;')}>
          {p.showSpark && (
            <span title={p.sparkTitle} style={sx('display:flex;align-items:flex-end;gap:2px;height:22px;')}>
              {p.spark.map((s, k) => (
                <span key={k} style={sx(s)} />
              ))}
            </span>
          )}
          <span style={sx('display:flex;flex-direction:column;align-items:flex-end;min-width:0;')}>
            <span style={sx(p.lastStyle)}>{p.last}</span>
            {p.showSpark && <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{p.cap}</span>}
          </span>
          <span style={sx('font-size:16px;color:#54606C;width:12px;')}>{p.open ? '▾' : '▸'}</span>
        </a>
      </div>
      {p.children}
    </div>
  );
}

/** An opened guard: what it does, its settings, its trip responses and the last it caught. */
function GuardDetail({
  g,
  scope,
  facilities,
  armed,
  canNet,
  edits,
  onEdit,
  who,
  segStyle,
}: {
  g: PolicyGuard;
  scope: string | undefined;
  /** The facility switcher's pick on the network scope: the catches follow it. */
  facilities?: string;
  armed: boolean;
  canNet: boolean;
  edits: Record<string, string | number | boolean>;
  onEdit: (k: string, v: string | number | boolean) => void;
  who: string;
  segStyle: (on: boolean, tone: string) => string;
}) {
  const demoBuilding = useDemoBuilding();
  const copy = GUARD_COPY[g.key];
  const catches = useApi((c, signal) => policyGuardCatches(c, { params: { key: g.key }, query: { scope, limit: 3, facilities }, signal }), [g.key, scope, facilities]);
  const act = (edits[g.key + ':act'] as string | undefined) ?? g.network.action;
  const valOf = (label: string) => (edits[g.key + ':' + label] !== undefined ? edits[g.key + ':' + label] : g.network.values[label]);
  const effect = (g.status ? 'Right now: ' + g.status + '. ' : '') + (act ? 'When it trips, it will ' + act.toLowerCase() + ': ' + (copy?.chain ?? '') + '.' : 'No trip response is set yet: ' + (copy?.chain ?? '') + '.');
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:14px 26px;padding:10px 0 6px;min-width:0;animation:riseIn .24s cubic-bezier(.22,.61,.36,1);')}>
      <div style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>WHAT IT DOES</span>
        <span style={sx('font-size:16px;color:#22272E;line-height:1.5;text-wrap:pretty;')}>{effect}</span>
        {(copy?.fields ?? []).map((f) => {
          const cur = valOf(f.label);
          const edited = edits[g.key + ':' + f.label] !== undefined;
          const pool = /-of-(\d+)/.exec(f.unit)?.[1];
          const label = f.label.charAt(0) + f.label.slice(1).toLowerCase();
          const sw2 = switchStyles(!!cur);
          return (
            <div key={f.label} style={sx('display:flex;align-items:center;gap:10px;min-width:0;')}>
              <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#3C454F;')}>{label}</span>
              {f.kind === 'step' && (
                <span style={sx('display:inline-flex;align-items:center;border:1px solid rgba(34,39,46,.18);border-radius:10px;overflow:hidden;flex:0 0 auto;')}>
                  {canNet && (
                    <a href="#" aria-label={'Lower ' + label} onClick={(e) => { pre(e); const n = Number(cur ?? 0) - 1; onEdit(g.key + ':' + f.label, pool ? Math.max(1, Math.min(Number(pool), n)) : Math.max(0, n)); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;font-size:17px;font-weight:700;color:#264C7E;text-decoration:none;')}>
                      {'−'}
                    </a>
                  )}
                  <span style={sx('min-width:62px;text-align:center;font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + (edited ? '#3C454F' : '#22272E') + ';padding:0 6px;')}>
                    {cur === undefined || cur === null ? '–' : String(cur)} <span style={sx('font-size:14px;font-weight:600;color:#54606C;')}>{f.unit}</span>
                  </span>
                  {canNet && (
                    <a href="#" aria-label={'Raise ' + label} onClick={(e) => { pre(e); const n = Number(cur ?? 0) + 1; onEdit(g.key + ':' + f.label, pool ? Math.max(1, Math.min(Number(pool), n)) : n); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;font-size:17px;font-weight:700;color:#264C7E;text-decoration:none;')}>
                      +
                    </a>
                  )}
                </span>
              )}
              {f.kind === 'toggle' && (
                <a href="#" role="switch" aria-checked={!!cur} aria-label={label} aria-disabled={!canNet || undefined} onClick={(e) => { pre(e); if (canNet) onEdit(g.key + ':' + f.label, !cur); }} style={sx(sw2.wrap)}>
                  <span style={sx(sw2.knob)} />
                </a>
              )}
              {f.kind === 'text' && <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{cur === undefined || cur === null ? '–' : String(cur)}</span>}
            </div>
          );
        })}
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>WHEN IT TRIPS</span>
        <span style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
          {(copy?.actions ?? []).map((n2) => (
            <a key={n2} href="#" aria-pressed={n2 === act} aria-disabled={!canNet || undefined} onClick={(e) => { pre(e); if (canNet) onEdit(g.key + ':act', n2); }} style={sx(segStyle(n2 === act, ACTION_TONE[n2] ?? '#2F5C97'))}>
              {n2}
            </a>
          ))}
        </span>
        <span style={sx('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{who}</span>
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;')}>
        <span style={sx('display:flex;align-items:baseline;gap:8px;')}>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>LAST CAUGHT</span>
          {catches.data?.real === false && !demoBuilding && <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;opacity:.8;')}>SAMPLE</span></BuildLabel>}
        </span>
        {catches.loading && !catches.data && <Skeleton rows={2} label="Loading catches" />}
        {catches.error && !catches.data && <ErrorNote error={catches.error} onRetry={catches.reload} compact />}
        {catches.data && catches.data.catches === null && <span style={sx('font-size:16px;color:#54606C;')}>{catches.data.reason ?? 'Not measured yet.'}</span>}
        {catches.data && catches.data.catches && catches.data.catches.length === 0 && (
          <span style={sx('font-size:16px;color:#54606C;')}>{armed ? 'Has not fired in the last 90 days.' : 'Switched off, so it cannot fire.'}</span>
        )}
        {(catches.data?.catches ?? []).map((c, k) => (
          <span key={k} style={sx('display:flex;flex-direction:column;gap:1px;padding:5px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
            <span style={sx('display:flex;align-items:baseline;gap:8px;')}>
              <b style={sx('font-size:16px;color:#22272E;')}>{[c.resident?.initials, c.resident?.room ? 'Rm ' + c.resident.room : null].filter(Boolean).join(' · ') || 'No resident'}</b>
              <span style={sx('font-size:14px;color:#54606C;margin-left:auto;white-space:nowrap;')}>{ago(c.at)}</span>
            </span>
            <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>{c.what ?? c.excerpt ?? ''}</span>
            {c.action && <span style={sx('font-size:14px;font-weight:700;color:#2F5C97;')}>{'→'} {c.action}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/** An opened custom rule: its sentence, and what it would have caught (real replay). */
function RuleDetail({ r, segStyle }: { r: PolicyRule; segStyle: (on: boolean, tone: string) => string }) {
  const replay = useApi((c, signal) => policyRuleReplay(c, { params: { id: r.id }, query: { days: 7 }, signal }), [r.id], r.status === 'live');
  const head = r.mode === 'watch' ? 'WOULD HAVE CAUGHT' : 'CAUGHT';
  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:14px 26px;padding:10px 0 6px;min-width:0;')}>
      <div style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>WHAT IT DOES</span>
        <span style={sx('font-size:16px;color:#22272E;line-height:1.5;text-wrap:pretty;')}>{ruleSentence(r) + (r.mode === 'watch' ? ' Right now it only records what it would have done.' : '')}</span>
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:8px;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>WHEN IT TRIPS</span>
        <span style={sx('display:flex;flex-wrap:wrap;gap:6px;')}>
          <span style={sx(segStyle(true, '#2F5C97'))}>{ACTIONS_SHORT_OF(r.action)}</span>
        </span>
        <span style={sx('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
          {r.overlaps.guards.length ? 'Overlaps ' + r.overlaps.guards.map((x) => x.name).join(', ') + '. When they disagree, the stricter action wins.' : 'No built-in guard watches this signal.'}
        </span>
      </div>
      <div style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>{head}</span>
        {r.status !== 'live' && <span style={sx('font-size:16px;color:#54606C;')}>Replay runs once Aithora approves the rule.</span>}
        {replay.loading && !replay.data && r.status === 'live' && <Skeleton rows={2} label="Replaying" />}
        {replay.error && !replay.data && <ErrorNote error={replay.error} onRetry={replay.reload} compact />}
        {replay.data && !replay.data.supported && (
          <span style={sx('font-size:16px;color:#54606C;')}>{[...replay.data.unsupportedSignals.map((u) => u.reason), replay.data.unsupportedAudience?.reason].filter(Boolean).join(' ')}</span>
        )}
        {replay.data && replay.data.supported && replay.data.catches.length === 0 && <span style={sx('font-size:16px;color:#54606C;')}>No matches in the last 7 days.</span>}
        {(replay.data?.catches ?? []).slice(0, 3).map((c, k) => (
          <span key={k} style={sx('display:flex;flex-direction:column;gap:1px;padding:5px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
            <span style={sx('display:flex;align-items:baseline;gap:8px;')}>
              <b style={sx('font-size:16px;color:#22272E;')}>{[c.resident.initials, c.resident.room ? 'Rm ' + c.resident.room : null].filter(Boolean).join(' · ')}</b>
              <span style={sx('font-size:14px;color:#54606C;margin-left:auto;white-space:nowrap;')}>{dayClock(c.at)}</span>
            </span>
            {c.excerpt && <span style={sx('font-size:14px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>{c.excerpt}</span>}
            <span style={sx('font-size:14px;font-weight:700;color:#2F5C97;')}>
              {'→'} {(c.wouldHaveOnly ? 'Would have: ' : '') + ACTIONS_SHORT_OF(c.action)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

const ACTIONS_SHORT_OF = (a: string) => (ACTIONS_SHORT as Record<string, string>)[a] ?? a;
